import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Clock, Users, Calendar, BookOpen, Star, MapPin, CheckCircle,
  Loader2, Home, Utensils, X, XCircle, TrendingUp, Play, Image as ImageIcon, FileText,
  Sparkles,
} from 'lucide-react';
import SEO from '../components/SEO';
import HowToReach from '../components/HowToReach';
import { courseService, Course, CourseReview } from '../services/courseService';
import { schoolService, School } from '../services/schoolService';
import { useAuth } from '../contexts/AuthContext';
import { bookingService } from '../services/bookingService';
import { mediaService } from '../services/mediaService';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import '../assets/css/CourseDetails.css';

const CourseDetails = () => {
  const { slug, courseSlug } = useParams<{ slug: string; courseSlug: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const stickyRef = useRef<HTMLDivElement>(null);
  const faqSectionRef = useRef<HTMLElement>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [school, setSchool] = useState<School | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookingError, setBookingError] = useState('');
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    selectedMonth: '',
    accommodationType: '',
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submittingBooking, setSubmittingBooking] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [roomHeroIndex, setRoomHeroIndex] = useState<Record<number, number>>({});
  const [mediaUrls, setMediaUrls] = useState<{ images: string[]; videos: string[] }>({ images: [], videos: [] });
  const errorMessageRef = useRef<HTMLDivElement>(null);
  const heroCTARef = useRef<HTMLDivElement>(null);
  const [showMobileCta, setShowMobileCta] = useState(false);
  const [reviews, setReviews] = useState<CourseReview[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 0, comment: '' });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewSuccess, setReviewSuccess] = useState(false);

  // Show fixed mobile CTA when the hero "Book This Course" button scrolls out of view
  useEffect(() => {
    const el = heroCTARef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowMobileCta(!entry.isIntersecting);
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, course]);

  // Lock body scroll when booking modal or video modal is open
  useBodyScrollLock(showBookingModal || !!selectedVideo || !!selectedImage);

  // Sticky sidebar: fixed until FAQ section appears, then stops (container-bound). Desktop only.
  useEffect(() => {
    if (loading || !course || !stickyRef.current || window.innerWidth < 1024) return;

    const element = stickyRef.current;
    const stickyTop = 96; // px offset from top
    let stickyState: 'static' | 'fixed' | 'absolute' = 'static';

    const updatePosition = () => {
      const container = element.parentElement;
      if (!container) return;

      const mainContent = document.querySelector('.course-details-main-content');
      const faqSection = faqSectionRef.current ?? document.querySelector<HTMLElement>('[data-course-details-faq]');
      const scrollY = window.scrollY || window.pageYOffset;
      const rect = container.getBoundingClientRect();
      const elementHeight = element.offsetHeight;
      const containerTop = rect.top + scrollY;

      // Boundary: stop sticking when FAQ section comes into view (use FAQ top as bottom limit)
      let stickBottomLimit: number;
      if (faqSection) {
        const faqRect = faqSection.getBoundingClientRect();
        stickBottomLimit = faqRect.top + scrollY;
      } else {
        stickBottomLimit = (mainContent
          ? mainContent.getBoundingClientRect().height + mainContent.getBoundingClientRect().top + scrollY
          : containerTop + 1e6) - 1;
      }

      if (mainContent) {
        const mainHeight = mainContent.getBoundingClientRect().height;
        container.style.minHeight = `${mainHeight}px`;
      }

      const fixedTop = scrollY + stickyTop;
      const fixedBottom = fixedTop + elementHeight;

      let targetState: 'static' | 'fixed' | 'absolute' = 'static';
      if (fixedTop < containerTop) {
        targetState = 'static';
      } else if (fixedBottom > stickBottomLimit) {
        targetState = 'absolute';
      } else {
        targetState = 'fixed';
      }

      const containerBottom = rect.bottom + scrollY;
      if (targetState === 'absolute') {
        const topInContainer = Math.min(
          stickBottomLimit - containerTop - elementHeight,
          containerBottom - containerTop - elementHeight
        );
        element.style.position = 'absolute';
        element.style.top = `${Math.max(0, topInContainer)}px`;
        element.style.left = '0';
        element.style.width = '100%';
        element.style.bottom = '';
        element.style.zIndex = '10';
        stickyState = targetState;
      } else if (targetState !== stickyState) {
        stickyState = targetState;
        if (targetState === 'fixed') {
          element.style.position = 'fixed';
          element.style.top = `${stickyTop}px`;
          element.style.left = `${rect.left}px`;
          element.style.width = `${rect.width}px`;
          element.style.bottom = '';
          element.style.zIndex = '10';
        } else {
          element.style.position = 'static';
          element.style.top = '';
          element.style.left = '';
          element.style.width = '';
          element.style.bottom = '';
          element.style.zIndex = '';
        }
      } else if (targetState === 'fixed') {
        element.style.left = `${rect.left}px`;
        element.style.width = `${rect.width}px`;
      }
    };

    const rafId = requestAnimationFrame(() => updatePosition());
    window.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition, { passive: true });
    window.addEventListener('load', updatePosition);

    const resizeObs = new ResizeObserver(() => updatePosition());
    const mainContent = document.querySelector('.course-details-main-content');
    if (mainContent) resizeObs.observe(mainContent);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('load', updatePosition);
      resizeObs.disconnect();
    };
  }, [course, school, loading]);

  useEffect(() => {
    if (courseSlug) {
      fetchCourseDetails();
    }
  }, [courseSlug, slug]);

  useEffect(() => {
    if (!course?._id) return;
    let cancelled = false;
    setReviewsLoading(true);
    courseService.getCourseReviews(course._id, 1, 20).then((res) => {
      if (!cancelled && res.success && res.data?.reviews) setReviews(res.data.reviews);
    }).catch(() => {
      if (!cancelled) setReviews([]);
    }).finally(() => {
      if (!cancelled) setReviewsLoading(false);
    });
    return () => { cancelled = true; };
  }, [course?._id]);

  const fetchCourseDetails = async () => {
    setLoading(true);
    setError('');
    let courseData: Course | null = null;
    
    try {
      const response = await courseService.getCourseBySlug(courseSlug!);
      if (response.success && response.data) {
        courseData = response.data.course;
        setCourse(courseData);
        
        // Convert media fileKeys to signed URLs
        if (courseData.media) {
          const convertedMedia = await mediaService.convertMediaToUrls(courseData.media);
          setMediaUrls({
            images: convertedMedia.images || [],
            videos: convertedMedia.videos || []
          });
        }
      } else {
        setError('Course not found');
        setLoading(false);
        return;
      }
      
      if (slug) {
        try {
          const schoolResponse = await schoolService.getSchoolById(slug);
          if (schoolResponse.success && schoolResponse.data) {
            const schoolData = (schoolResponse.data as any).school || schoolResponse.data;
            setSchool(schoolData);
          }
        } catch (err) {
          console.error('Failed to fetch school:', err);
          if (courseData && courseData.schoolId) {
            try {
              const schoolId = typeof courseData.schoolId === 'string' 
                ? courseData.schoolId 
                : (courseData.schoolId as any)?._id || (courseData.schoolId as any)?.id;
              
              if (schoolId && typeof schoolId === 'string') {
                const fallbackResponse = await schoolService.getSchoolById(schoolId);
                if (fallbackResponse.success && fallbackResponse.data) {
                  const schoolData = (fallbackResponse.data as any).school || fallbackResponse.data;
                  setSchool(schoolData);
                }
              }
            } catch (fallbackErr) {
              console.error('Failed to fetch school with fallback method:', fallbackErr);
            }
          }
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load course details.');
    } finally {
      setLoading(false);
    }
  };


  const handleReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!course || !isAuthenticated || reviewForm.rating < 1 || reviewForm.rating > 5) {
      setReviewError('Please select a star rating (1–5).');
      return;
    }
    setReviewSubmitting(true);
    setReviewError('');
    setReviewSuccess(false);
    try {
      await courseService.createCourseReview(course._id, {
        rating: reviewForm.rating,
        comment: reviewForm.comment.trim() || undefined,
      });
      setReviewSuccess(true);
      setReviewForm({ rating: 0, comment: '' });
      const [reviewsRes, courseRes] = await Promise.all([
        courseService.getCourseReviews(course._id, 1, 20),
        courseService.getCourseById(course._id),
      ]);
      if (reviewsRes.success && reviewsRes.data?.reviews) setReviews(reviewsRes.data.reviews);
      if (courseRes.success && courseRes.data?.course) setCourse(courseRes.data.course);
    } catch (err: any) {
      setReviewError(err.message || 'Failed to submit review.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleBookCourse = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: `/schools/${slug}/${courseSlug}` } } });
      return;
    }
    setBookingError('');
    setBookingForm({
      selectedMonth: '',
      accommodationType: '',
      name: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '',
      email: user?.email || '',
      phone: user?.phone || '',
      message: '',
    });
    setShowBookingModal(true);
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!course || !school || !user) {
      setBookingError('Please fill in all required fields');
      return;
    }

    const bookingNeedsAccommodation =
      Boolean(course.accommodation?.available) &&
      Array.isArray(course.accommodation?.rooms) &&
      course.accommodation.rooms.length > 0;
    if (bookingNeedsAccommodation && !bookingForm.accommodationType.trim()) {
      setBookingError('Please select an accommodation type.');
      return;
    }

    setSubmittingBooking(true);
    setBookingError('');
    try {
      const bookingPayload: any = {
        schoolId: school._id,
        courseId: course._id,
        selectedMonth: bookingForm.selectedMonth,
        name: bookingForm.name,
        email: bookingForm.email,
        phone: bookingForm.phone,
      };

      const msg = bookingForm.message.trim();
      if (msg) {
        bookingPayload.message = msg;
      }

      if (bookingForm.accommodationType.trim() !== '') {
        bookingPayload.accommodationType = bookingForm.accommodationType.trim();
      }
      
      await bookingService.createBooking(bookingPayload);
      setShowBookingModal(false);
      setBookingError('');
      navigate('/bookings');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create booking';
      setBookingError(errorMessage);
      setTimeout(() => {
        errorMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } finally {
      setSubmittingBooking(false);
    }
  };

  const getBookingPeriodOptions = () => {
    const options: { value: string; label: string }[] = [];
    const today = new Date().toISOString().slice(0, 10);
    const ranges = course?.schedule?.dateRanges ?? [];

    const fmt = (iso: string) =>
      new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

    if (ranges.length > 0) {
      for (const r of ranges) {
        const start = typeof r.startDate === 'string' ? r.startDate.slice(0, 10) : new Date(r.startDate).toISOString().slice(0, 10);
        const end = typeof r.endDate === 'string' ? r.endDate.slice(0, 10) : new Date(r.endDate).toISOString().slice(0, 10);
        if (end < today) continue;
        options.push({
          value: `${start}|${end}`,
          label: `${fmt(start)} – ${fmt(end)}`,
        });
      }
      return options;
    }

    const todayDate = new Date();
    const activeMonths = course?.schedule?.activeMonths;
    for (let i = 0; i < 12; i++) {
      const date = new Date(todayDate.getFullYear(), todayDate.getMonth() + i, 1);
      const monthNumber = date.getMonth() + 1;
      if (activeMonths?.length && !activeMonths.includes(monthNumber)) continue;
      const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      const monthValue = `${date.getFullYear()}-${String(monthNumber).padStart(2, '0')}`;
      options.push({ value: monthValue, label: monthName });
    }
    return options;
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getDifficultyColor = (difficulty?: string) => {
    switch (difficulty) {
      case 'BEGINNER':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'INTERMEDIATE':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'ADVANCED':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      default:
        return 'bg-blue-50 text-blue-700 border-blue-200';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'TRAINING':
        return 'bg-violet-50 text-violet-700 border-violet-200';
      case 'RETREAT':
        return 'bg-pink-50 text-pink-700 border-pink-200';
      case 'WORKSHOP':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'CLASS':
        return 'bg-cyan-50 text-cyan-700 border-cyan-200';
      default:
        return 'bg-primary/10 text-primary border-primary/20';
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-fog">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-orange mx-auto mb-4" />
          <p className="text-midnight/60">Loading course details...</p>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
      return;
    }
    if (slug) {
      navigate(`/schools/${slug}`);
      return;
    }
    navigate('/');
  };

  if (error || !course) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-fog">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-5 font-semibold text-gray-900 mb-2">Course Not Found</h2>
            <p className="text-gray-600 mb-6">{error || 'The course you are looking for does not exist.'}</p>
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-primary hover:text-primary-dark font-semibold transition-colors"
              type="button"
            >
              <ArrowLeft className="h-5 w-5" />
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${course.name} - ${school?.name || 'Course'} - Book My Yoga TTC`}
        description={course.shortDescription || course.description}
        url={`https://bookmyyogattc.com/schools/${slug}/${courseSlug}`}
      />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 course-details-wrapper">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-forest via-forest-dark to-forest pt-24 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              onClick={handleBack}
              className="inline-flex items-center text-white/90 hover:text-white mb-6 transition-colors group"
              type="button"
            >
              <ArrowLeft className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform" />
              Back
            </button>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-5 md:p-6"
            >
              <div className="flex flex-col lg:flex-row gap-5">
                {/* Left: Course Info */}
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className={`px-3 py-1 rounded-full text-1 font-semibold border-2 ${getTypeColor(course.type)}`}>
                      {course.type}
                    </span>
                    {course.difficulty && (
                      <span className={`px-3 py-1 rounded-full text-1 font-semibold border-2 ${getDifficultyColor(course.difficulty)}`}>
                        {course.difficulty}
                      </span>
                    )}
                    {course.rating > 0 && (
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-yellow-50 rounded-full border-2 border-yellow-200">
                        <Star className="h-3.5 w-3.5 fill-yellow-500 text-yellow-500" />
                        <span className="text-1 font-semibold text-yellow-700">{course.rating.toFixed(1)}</span>
                        <span className="text-1 text-yellow-600">({course.totalReviews})</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Yoga Styles */}
                  {course.yogaStyles && course.yogaStyles.length > 0 && (
                    <div className="mb-3">
                      <p className="text-1 text-gray-600 font-medium">
                        Yoga Styles: {course.yogaStyles.join(', ')}
                      </p>
                    </div>
                  )}
                  
                  <h1 className="text-5 md:text-5 font-semibold text-gray-900 mb-2.5 leading-tight">
                    {course.name}
                  </h1>
                  
                  {course.shortDescription && (
                    <p className="text-2 text-gray-600 mb-3.5 leading-relaxed">
                      {course.shortDescription}
                    </p>
                  )}

                  <div className="flex flex-wrap items-center gap-3 text-gray-600 mb-4">
                    {school && (
                      <div className="flex items-center gap-1.5">
                        <div className="p-1.5 bg-primary/10 rounded-lg">
                          <MapPin className="h-4 w-4 text-primary" />
                        </div>
                        <span className="font-medium text-2">{school.location}, {school.city}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5">
                      <div className="p-1.5 bg-primary/10 rounded-lg">
                        <Clock className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-medium text-2">{course.duration} days</span>
                    </div>
                    {course.maxStudents && (
                      <div className="flex items-center gap-1.5">
                        <div className="p-1.5 bg-primary/10 rounded-lg">
                          <Users className="h-4 w-4 text-primary" />
                        </div>
                        <span className="font-medium text-2">Max {course.maxStudents} students</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: CTA Card */}
                <div ref={heroCTARef} className="lg:w-72 flex-shrink-0">
                  <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-5 border-2 border-primary/20 sticky top-24">
                    {course.price && (
                      <div className="mb-5">
                        <p className="text-1 text-gray-600 mb-1">Starting from</p>
                        <p className="text-5 font-semibold text-primary mb-1">
                          {formatCurrency(course.price, course.currency)}
                        </p>
                        <p className="text-1 text-gray-500">for {course.duration} days</p>
                      </div>
                    )}
                    <div className="space-y-2.5">
                      <button
                        onClick={handleBookCourse}
                        className="w-full flex items-center justify-center gap-2 bg-primary text-white px-5 py-3 rounded-xl font-semibold text-2 hover:bg-primary-dark transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                      >
                        <Calendar className="h-4 w-4" />
                        Book This Course
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Main Content - pb-24 on mobile for fixed CTA clearance */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 lg:py-8 lg:pb-12 course-details-content-wrapper">
          <div className="course-details-container">
            {/* Main Content Column — flat sections, thin dividers */}
            <div className="flex min-w-0 flex-col divide-y divide-gray-200 bg-white rounded-2xl shadow-lg border p-5 border-gray-100 course-details-main-content">
              {/* Course Media Collage */}
              {(course.media?.images && course.media.images.length > 0) || (course.media?.videos && course.media.videos.length > 0) ? (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="py-4 md:py-5"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <ImageIcon className="h-5 w-5 text-primary" />
                    </div>
                    <h2 className="text-5 md:text-5 font-semibold text-gray-900">Course Gallery</h2>
                  </div>
                  
                  {/* Media Collage - Fashionable Masonry Layout */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                    {/* Images with varied heights for masonry effect */}
                    {mediaUrls.images && mediaUrls.images.map((imageUrl, index) => {
                      // Create varied aspect ratios for a more dynamic layout
                      const aspectRatios = ['aspect-square', 'aspect-[4/5]', 'aspect-[5/4]'];
                      const aspectClass = aspectRatios[index % aspectRatios.length];
                      
                      return (
                        <motion.div
                          key={`img-${index}`}
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          onClick={() => setSelectedImage(imageUrl)}
                          className={`relative group cursor-pointer overflow-hidden rounded-xl ${aspectClass} shadow-md hover:shadow-xl transition-all duration-300`}
                        >
                          <img
                            src={imageUrl}
                            alt={`Course image ${index + 1}`}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              // Only set fallback if it's not already the fallback
                              if (!target.src.includes('placeholder')) {
                                target.src = 'https://via.placeholder.com/400?text=Image+Not+Found';
                              } else {
                                target.style.display = 'none';
                              }
                              e.stopPropagation();
                            }}
                            onLoad={(e) => {
                              // Image loaded successfully
                              const target = e.target as HTMLImageElement;
                              target.style.opacity = '1';
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="bg-white/95 backdrop-blur-sm rounded-lg p-2 shadow-lg">
                              <ImageIcon className="h-4 w-4 text-gray-700" />
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                    
                    {/* Videos with varied heights */}
                    {mediaUrls.videos && mediaUrls.videos.map((videoUrl, index) => {
                      // Create varied aspect ratios for videos too
                      const aspectRatios = ['aspect-square', 'aspect-[4/5]', 'aspect-[5/4]'];
                      const aspectClass = aspectRatios[index % aspectRatios.length];
                      
                      return (
                        <motion.div
                          key={`vid-${index}`}
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: (course.media?.images?.length || 0) * 0.1 + index * 0.1 }}
                          onClick={() => setSelectedVideo(videoUrl)}
                          className={`relative group cursor-pointer overflow-hidden rounded-xl ${aspectClass} bg-gradient-to-br from-primary/20 via-primary/30 to-primary/40 shadow-md hover:shadow-xl transition-all duration-300`}
                        >
                          <div className="absolute inset-0 flex items-center justify-center z-10">
                            <div className="bg-white/95 backdrop-blur-sm rounded-full p-5 group-hover:scale-110 transition-transform duration-300 shadow-2xl border-4 border-white/50">
                              <Play className="h-10 w-10 text-primary fill-primary" />
                            </div>
                          </div>
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />
                          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                            <div className="bg-white/95 backdrop-blur-sm rounded-lg p-2 shadow-lg">
                              <Play className="h-4 w-4 text-gray-700 fill-gray-700" />
                            </div>
                          </div>
                          {/* Video thumbnail */}
                          <video
                            src={videoUrl}
                            className="w-full h-full object-cover"
                            muted
                            playsInline
                            preload="metadata"
                            onError={(e) => {
                              const target = e.target as HTMLVideoElement;
                              // Hide video on error, show placeholder
                              target.style.display = 'none';
                              e.stopPropagation();
                            }}
                          />
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.section>
              ) : null}

              {/* Course Description */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="py-4 md:py-5"
              >
                <h2 className="text-5 md:text-5 font-semibold text-gray-900 mb-3">About This Course</h2>
                <div className="text-2 text-gray-700 leading-relaxed">
                  {course.description ? (
                    <p className="whitespace-pre-wrap">{course.description}</p>
                  ) : (
                    <p>{course.shortDescription || 'Join us for an immersive journey into the ancient practice of yoga.'}</p>
                  )}
                </div>
              </motion.section>

              {/* Yoga Styles Section */}
              {course.yogaStyles && course.yogaStyles.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="py-4 md:py-5"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <BookOpen className="h-5 w-5 text-primary" />
                    </div>
                    <h2 className="text-5 md:text-5 font-semibold text-gray-900">Yoga Styles Taught</h2>
                  </div>
                  <p className="text-2 text-gray-600 mb-4 leading-relaxed">
                    This course covers the following yoga styles and traditions:
                  </p>
                  <ul className="space-y-2.5">
                    {course.yogaStyles.map((style, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-2 leading-relaxed">{style}</span>
                      </li>
                    ))}
                  </ul>
                </motion.section>
              )}

              {/* Who Is This For */}
              {course.whoIsThisFor && course.whoIsThisFor.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="py-4 md:py-5"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Users className="h-5 w-5 text-primary" />
                    </div>
                    <h2 className="text-5 md:text-5 font-semibold text-gray-900">Who Is This For?</h2>
                  </div>
                  <ul className="space-y-2.5">
                    {course.whoIsThisFor.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-2 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.section>
              )}

              {/* Course Highlights */}
              {course.courseHighlights && course.courseHighlights.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="py-4 md:py-5"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-yellow-50 rounded-lg">
                      <Star className="h-5 w-5 text-yellow-600 fill-yellow-600" />
                    </div>
                    <h2 className="text-5 md:text-5 font-semibold text-gray-900">Course Highlights</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {course.courseHighlights.map((highlight, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <Star className="h-5 w-5 text-yellow-600 fill-yellow-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-2 leading-relaxed">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </motion.section>
              )}

              {/* What You Will Gain */}
              {course.whatYouWillGain && course.whatYouWillGain.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="py-4 md:py-5"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-emerald-50 rounded-lg">
                      <TrendingUp className="h-5 w-5 text-emerald-600" />
                    </div>
                    <h2 className="text-5 md:text-5 font-semibold text-gray-900">What You Will Gain</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {course.whatYouWillGain.map((benefit, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <TrendingUp className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-2 leading-relaxed">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </motion.section>
              )}

              {/* Detailed Curriculum */}
              {course.curriculum && course.curriculum.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="py-4 md:py-5"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-violet-50 rounded-lg">
                      <BookOpen className="h-5 w-5 text-violet-600" />
                    </div>
                    <h2 className="text-5 md:text-5 font-semibold text-gray-900">Detailed Curriculum</h2>
                  </div>
                  <div className="space-y-5">
                    {course.curriculum.map((section, index) => (
                      <div key={index} className="border-l-3 border-primary pl-4 py-1.5">
                        <h3 className="text-4 font-semibold text-gray-900 mb-3">{section.title}</h3>
                        <ul className="space-y-1.5">
                          {section.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start gap-2.5 text-gray-700 text-2 leading-relaxed">
                              <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </motion.section>
              )}

              {/* Daily Schedule */}
              {course.schedule?.timeSlots && course.schedule.timeSlots.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="py-4 md:py-5"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <Clock className="h-5 w-5 text-blue-600" />
                    </div>
                    <h2 className="text-5 md:text-5 font-semibold text-gray-900">Daily Schedule</h2>
                  </div>
                  <div className="overflow-hidden rounded-xl border border-dashed border-gray-300">
                    <div className="grid grid-cols-2 bg-gray-50 border-b border-dashed border-gray-300 text-1 font-semibold text-gray-700">
                      <div className="px-4 py-2">Time</div>
                      <div className="px-4 py-2">Label</div>
                    </div>
                    <div className="divide-y divide-dashed divide-gray-200">
                      {course.schedule.timeSlots.map((slot, index) => (
                        <div key={index} className="grid grid-cols-2 items-center text-2">
                          <div className="px-4 py-3 flex items-center gap-2 text-gray-800 font-semibold">
                            <Clock className="h-3.5 w-3.5 text-primary" />
                            <span>{slot.startTime} - {slot.endTime}</span>
                          </div>
                          <div className="px-4 py-3 text-gray-700 text-2">
                            {slot.label || <span className="text-gray-400">—</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.section>
              )}

              {/* Timetable */}
              {course.timetable && course.timetable.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="py-4 md:py-5"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-indigo-50 rounded-lg">
                      <Calendar className="h-5 w-5 text-indigo-600" />
                    </div>
                    <h2 className="text-5 md:text-5 font-semibold text-gray-900">Daily Timetable</h2>
                  </div>
                  <ul className="space-y-2.5">
                    {course.timetable.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Clock className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-2 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.section>
              )}

              {/* Inclusions */}
              {course.inclusions && course.inclusions.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="py-4 md:py-5"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-green-50 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <h2 className="text-4 md:text-5 font-semibold text-gray-900">What's Included</h2>
                  </div>
                  <ul className="space-y-2">
                    {course.inclusions.map((inclusion, index) => (
                      <li key={index} className="flex items-start gap-2.5">
                        <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-2 leading-relaxed">{inclusion}</span>
                      </li>
                    ))}
                  </ul>
                </motion.section>
              )}

              {/* Requirements */}
              {course.requirements && course.requirements.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="py-4 md:py-5"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <h2 className="text-4 md:text-5 font-semibold text-gray-900">Requirements</h2>
                  </div>
                  <ul className="space-y-2">
                    {course.requirements.map((requirement, index) => (
                      <li key={index} className="flex items-start gap-2.5">
                        <FileText className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-2 leading-relaxed">{requirement}</span>
                      </li>
                    ))}
                  </ul>
                </motion.section>
              )}

              {/* Accommodation - rooms with hero image + thumbnail row */}
              {course.accommodation && course.accommodation.available && course.accommodation.rooms && course.accommodation.rooms.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="py-4 md:py-5"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Home className="h-5 w-5 text-primary" />
                    </div>
                    <h2 className="text-4 md:text-5 font-semibold text-gray-900">Accommodation</h2>
                  </div>
                  <div className="space-y-6">
                    {course.accommodation.rooms.map((room, idx) => {
                      const resolveUrl = (key: string) => mediaService.getMediaUrlFromKey(key);
                      const roomImages = (room.images || [])
                        .filter((x): x is string => typeof x === 'string' && !!x)
                        .map(resolveUrl)
                        .filter(Boolean);
                      const roomVideos = (room.videos || [])
                        .filter((x): x is string => typeof x === 'string' && !!x)
                        .map(resolveUrl)
                        .filter(Boolean);
                      const allMedia: { type: 'image' | 'video'; url: string }[] = [
                        ...roomImages.map(url => ({ type: 'image' as const, url })),
                        ...roomVideos.map(url => ({ type: 'video' as const, url })),
                      ];
                      const selectedIndex = roomHeroIndex[idx] ?? 0;
                      const currentHero = allMedia[selectedIndex];
                      return (
                        <div key={idx} className="border-b border-gray-200/80 pb-6 last:border-0 last:pb-0">
                          {/* Hero: large image or video (shows selected media) */}
                          <div className="aspect-[16/10] rounded-xl overflow-hidden bg-gray-100 mb-4">
                            {currentHero ? (
                              currentHero.type === 'image' ? (
                                <button type="button" className="w-full h-full block focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-xl overflow-hidden group" onClick={() => setSelectedImage(currentHero.url)}>
                                  <img src={currentHero.url} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300" />
                                </button>
                              ) : (
                                <button type="button" className="w-full h-full block focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-xl overflow-hidden bg-gray-900 relative group" onClick={() => setSelectedVideo(currentHero.url)}>
                                  <video src={currentHero.url} className="absolute inset-0 w-full h-full object-cover" muted playsInline />
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
                                      <Play className="h-7 w-7 text-primary fill-primary ml-0.5" />
                                    </span>
                                  </div>
                                </button>
                              )
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-300">
                                <Home className="h-16 w-16" />
                              </div>
                            )}
                          </div>
                          {/* Thumbnails in a row: clicking updates the big image */}
                          {allMedia.length > 1 && (
                            <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
                              {allMedia.map((item, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 transition-all ${i === selectedIndex ? 'border-primary ring-2 ring-primary/30' : 'border-gray-200 hover:border-gray-300'}`}
                                  onClick={() => setRoomHeroIndex(prev => ({ ...prev, [idx]: i }))}
                                >
                                  {item.type === 'image' ? (
                                    <img src={item.url} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <span className="w-full h-full bg-gray-800 flex items-center justify-center">
                                      <Play className="h-5 w-5 text-white fill-white" />
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                            <h3 className="text-2xl font-semibold text-gray-900 capitalize">
                              {room.type.replace(/-/g, ' ')}
                            </h3>
                            {room.price != null && (
                              <span className="text-xl font-semibold text-primary">
                                {formatCurrency(room.price, course.currency)}
                              </span>
                            )}
                          </div>
                          {room.capacity != null && (
                            <p className="text-gray-600 text-sm mb-2">
                              Up to {room.capacity} {room.capacity === 1 ? 'person' : 'people'}
                            </p>
                          )}
                          {room.description && room.description.trim() && (
                            <p className="text-gray-700 leading-relaxed mb-2">{room.description}</p>
                          )}
                          {room.amenities && room.amenities.length > 0 && (
                            <p className="text-gray-600 text-sm">Amenities: {room.amenities.join(', ')}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {course.accommodation.mealPlan && course.accommodation.mealPlan.type && (
                    <div className="mt-6 pt-6 border-t border-gray-200 flex items-center gap-2 text-gray-700">
                      <Utensils className="h-5 w-5 text-primary flex-shrink-0" />
                      <span>
                        Meal plan: {course.accommodation.mealPlan.type}
                        {course.accommodation.mealPlan.included && ' (included)'}
                      </span>
                    </div>
                  )}
                </motion.section>
              )}

              {/* How to Reach - inside left column, horizontal journey */}
              {(school?.howToReach?.stations?.length ?? 0) > 0 && (
                <HowToReach stations={school!.howToReach!.stations} />
              )}
            </div>

            {/* Sidebar - Fixed */}
            <aside className="course-details-sidebar-container">
              {/* Pricing & Accommodation Card */}
              <div 
                ref={stickyRef}
                className="course-details-sticky-wrapper"
              >
                <div
                  className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 course-details-sticky-sidebar"
                >
                <h2 className="text-5 font-semibold text-gray-900 mb-4">Pricing & Accommodation</h2>
                
                {course.price ? (
                  <div className="mb-5 pb-5 border-b border-gray-200">
                    <p className="text-1 text-gray-600 mb-1.5">Course Fee</p>
                    <p className="text-5 font-semibold text-primary mb-1">
                      {formatCurrency(course.price, course.currency)}
                    </p>
                    <p className="text-1 text-gray-500">for {course.duration} days</p>
                  </div>
                ) : (
                  <div className="mb-5 pb-5 border-b border-gray-200">
                    <p className="text-gray-600 text-1">Contact for pricing information</p>
                  </div>
                )}

                {course.accommodation && course.accommodation.available && (
                  <div className="mb-5">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-2">
                      <Home className="h-4 w-4 text-primary" />
                      Accommodation
                    </h3>
                    {course.accommodation.rooms && course.accommodation.rooms.length > 0 ? (
                      <ul className="space-y-3">
                        {course.accommodation.rooms.map((room, idx) => (
                          <li key={idx} className="flex flex-col gap-1">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-gray-900 capitalize text-2">{room.type}</span>
                              {room.price && (
                                <span className="text-3 font-semibold text-primary">
                                  {formatCurrency(room.price, course.currency)}
                                </span>
                              )}
                            </div>
                            {room.capacity && (
                              <div className="flex items-center gap-2 text-1 text-gray-600">
                                <Users className="h-3.5 w-3.5" />
                                <span>Capacity: {room.capacity}</span>
                              </div>
                            )}
                            {room.description && room.description.trim() && (
                              <p className="text-1 text-gray-600 leading-relaxed">{room.description}</p>
                            )}
                            {room.amenities && room.amenities.length > 0 && (
                              <p className="text-1 text-gray-600">Amenities: {room.amenities.join(', ')}</p>
                            )}
                          </li>
                        ))}
                        {course.accommodation.mealPlan && course.accommodation.mealPlan.type && (
                          <li className="flex items-center gap-2 text-1 text-gray-700 pt-2 border-t border-gray-200">
                            <Utensils className="h-3.5 w-3.5 text-primary" />
                            <span>
                              <strong>Meal Plan:</strong> {course.accommodation.mealPlan.type}
                              {course.accommodation.mealPlan.included && ' (Included)'}
                            </span>
                          </li>
                        )}
                      </ul>
                    ) : (
                      <p className="text-1 text-gray-600">Accommodation available - contact for details</p>
                    )}
                  </div>
                )}

                {/* Book Your Seat Button */}
                <button
                  onClick={handleBookCourse}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-white px-5 py-3 rounded-xl font-semibold text-2 hover:bg-primary-dark transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 mt-5"
                >
                  <Calendar className="h-4 w-4" />
                  Book Your Seat Now
                </button>
                </div>
              </div>
            </aside>
          </div>

          {/* FAQ Section — card grid (same visual language as /consultation; not an accordion) */}
          <motion.section
            ref={faqSectionRef}
            data-course-details-faq
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto mt-6 w-full max-w-4xl border-t border-gray-200 pt-6 md:mt-8 md:pt-8"
          >
            <div className="mb-4 text-center sm:mb-5">
              <h2 className="heading-section mb-2 font-serif font-semibold text-midnight">
                Frequently asked questions
              </h2>
              <p className="mx-auto max-w-xl text-2 leading-relaxed text-midnight/60">
                Common questions about this course, fees, certification, and your stay.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
              {(course.faq && course.faq.length > 0
                ? course.faq
                : [
                    {
                      question: 'What is included in the course fee?',
                      answer: course.price
                        ? `The course fee of ${formatCurrency(course.price, course.currency)} includes all training materials, certification, accommodation (if specified), and meals (if included in the package).`
                        : 'The course fee includes all training materials, certification, accommodation (if specified), and meals (if included in the package).',
                    },
                    {
                      question: 'Do I need prior yoga experience?',
                      answer:
                        course.difficulty === 'BEGINNER'
                          ? 'No prior experience needed! This course is designed for beginners and welcomes practitioners of all levels.'
                          : 'While prior experience is helpful, our program welcomes practitioners of all levels who are committed to deepening their practice.',
                    },
                    {
                      question: 'Is the certification Yoga Alliance recognized?',
                      answer: school?.yogaAllianceCertified
                        ? 'Yes, our program is Yoga Alliance certified and recognized internationally.'
                        : 'Please contact us for certification details.',
                    },
                    {
                      question: 'What is the class size?',
                      answer: course.maxStudents
                        ? `We maintain small class sizes with a maximum of ${course.maxStudents} students to ensure personalized attention and quality instruction.`
                        : 'We maintain small class sizes to ensure personalized attention and quality instruction.',
                    },
                  ]
              ).map((faq, index) => (
                <motion.article
                  key={`${faq.question}-${index}`}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.04 }}
                  viewport={{ once: true }}
                  className="flex flex-col rounded-xl border border-gray-200/90 bg-transparent p-4 transition-all duration-200 hover:border-orange/40 sm:p-4"
                >
                  <div className="mb-3 flex gap-3">
                    <span
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-orange to-orange-dark text-white shadow-sm"
                      aria-hidden
                    >
                      <Sparkles className="h-5 w-5" strokeWidth={2} />
                    </span>
                    <h3 className="min-w-0 flex-1 text-left text-2 font-semibold leading-snug text-midnight">
                      {faq.question}
                    </h3>
                  </div>
                  <div
                    className="mb-3 h-px w-10 rounded-full bg-gradient-to-r from-orange/70 to-transparent"
                    aria-hidden
                  />
                  <p className="text-left text-2 leading-relaxed text-midnight/70">{faq.answer}</p>
                </motion.article>
              ))}
            </div>
          </motion.section>

          {/* Reviews Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-6 border-t border-gray-200 pt-6 md:mt-8 md:pt-8"
          >
            <h2 className="heading-section mb-4 font-semibold text-gray-900">Reviews</h2>

            {reviewsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                {reviews.length > 0 && (
                  <ul className="space-y-4 mb-8">
                    {reviews.map((r) => {
                      const u = r.userId as { _id?: string; firstName?: string; lastName?: string };
                      const name = u && typeof u === 'object' ? [u.firstName, u.lastName].filter(Boolean).join(' ') || 'User' : 'User';
                      return (
                        <li key={r._id} className="pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                          <div className="flex items-center gap-2 mb-1.5">
                            <div className="flex gap-0.5">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`h-4 w-4 ${star <= r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`}
                                />
                              ))}
                            </div>
                            <span className="font-semibold text-2 text-gray-900">{name}</span>
                            <span className="text-1 text-gray-500">
                              {new Date(r.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          {r.comment && <p className="text-2 text-gray-700 leading-relaxed">{r.comment}</p>}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {reviews.length === 0 && !isAuthenticated && (
                  <p className="text-2 text-gray-600 mb-6">No reviews yet. Log in to write the first one.</p>
                )}
                {reviews.length === 0 && isAuthenticated && (
                  <p className="text-2 text-gray-600 mb-6">No reviews yet. Be the first to review this course.</p>
                )}

                {isAuthenticated ? (
                  <form onSubmit={handleReviewSubmit} className="border-t border-gray-100 pt-6">
                    <p className="font-semibold text-gray-900 mb-2 text-2">Write a review</p>
                    <div className="mb-3">
                      <span className="text-2 text-gray-600 mr-2">Your rating:</span>
                      <div className="flex gap-1 inline-flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setReviewForm((f) => ({ ...f, rating: star }))}
                            className="p-0.5 rounded hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary/50"
                            aria-label={`${star} star${star > 1 ? 's' : ''}`}
                          >
                            <Star
                              className={`h-8 w-8 transition-colors ${star <= reviewForm.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="review-comment" className="block text-2 text-gray-700 mb-1">Comment (optional)</label>
                      <textarea
                        id="review-comment"
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm((f) => ({ ...f, comment: e.target.value }))}
                        placeholder="Share your experience..."
                        rows={3}
                        maxLength={2000}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      />
                    </div>
                    {reviewError && <p className="text-1 text-red-600 mb-2">{reviewError}</p>}
                    {reviewSuccess && <p className="text-1 text-green-600 mb-2">Thank you! Your review has been submitted.</p>}
                    <button
                      type="submit"
                      disabled={reviewSubmitting || reviewForm.rating < 1}
                      className="px-5 py-2.5 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-2"
                    >
                      {reviewSubmitting ? 'Submitting...' : 'Submit review'}
                    </button>
                  </form>
                ) : (
                  <p className="text-2 text-gray-600">
                    <button
                      type="button"
                      onClick={() => navigate('/login', { state: { from: { pathname: `/schools/${slug}/${courseSlug}` } } })}
                      className="text-primary font-semibold hover:underline"
                    >
                      Log in
                    </button>
                    {' '}to write a review.
                  </p>
                )}
              </>
            )}
          </motion.section>
        </div>
      </div>

      {/* Fixed mobile CTA - rendered via portal to avoid transform containment breaking position:fixed */}
      {showMobileCta && createPortal(
        <div className="lg:hidden fixed bottom-4 left-2 right-2 z-[999] pb-[env(safe-area-inset-bottom)]">
          <button
            onClick={handleBookCourse}
            className="w-full flex items-center justify-center gap-2 bg-primary text-white py-3.5 rounded-xl font-semibold text-2 hover:bg-primary-dark transition-all shadow-lg active:scale-[0.98]"
          >
            <Calendar className="h-4 w-4" />
            Book Your Seat Now
          </button>
        </div>,
        document.body
      )}

      {/* Booking Modal */}
      <AnimatePresence>
        {showBookingModal && (
          <div 
            className="modal-backdrop fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              touchAction: 'none',
              overflow: 'hidden',
              overscrollBehavior: 'none',
              WebkitOverflowScrolling: 'none' as any,
              margin: 0,
              padding: '1rem'
            }}
            onTouchStart={(e) => {
              // Prevent all touch events on backdrop
              if (e.target === e.currentTarget) {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }
            }}
            onTouchMove={(e) => {
              // Prevent backdrop scrolling
              if (e.target === e.currentTarget) {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }
            }}
            onTouchEnd={(e) => {
              // Prevent touch end events
              if (e.target === e.currentTarget) {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }
            }}
            onWheel={(e) => {
              // Prevent wheel events on backdrop
              if (e.target === e.currentTarget) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowBookingModal(false);
                setBookingError('');
              }
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full flex flex-col"
              style={{ 
                height: '95vh',
                maxHeight: '95vh',
                position: 'relative',
                zIndex: 10000
              }}
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => {
                // Allow touch events within modal for scrolling
                e.stopPropagation();
              }}
              onTouchMove={(e) => {
                // Allow touch events within modal for scrolling
                e.stopPropagation();
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
              }}
              onWheel={(e) => {
                // Allow wheel events within modal
                e.stopPropagation();
              }}
            >
            {/* Header - Fixed */}
            <div className="flex-shrink-0 bg-white border-b border-gray-200 p-5 flex items-center justify-between z-10 rounded-t-2xl">
              <h2 className="text-5 font-semibold text-gray-900">Book Course</h2>
              <button
                onClick={() => {
                  setShowBookingModal(false);
                  setBookingError('');
                }}
                className="text-gray-600 hover:text-gray-900 transition-colors p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Error Message - Fixed */}
            {bookingError && (
              <div ref={errorMessageRef} className="flex-shrink-0 px-6 pt-4 bg-white">
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border-2 border-red-300 rounded-lg p-4 shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-red-800 font-medium text-2">{bookingError}</p>
                  </div>
                </motion.div>
              </div>
            )}

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto overscroll-contain" style={{ 
              WebkitOverflowScrolling: 'touch' as any,
              touchAction: 'pan-y'
            }}>
              <form onSubmit={handleBookingSubmit} className="p-5 space-y-5">
              <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-4 border-2 border-primary/20">
                <h3 className="font-semibold text-gray-900 mb-1.5 text-2">{course.name}</h3>
                <p className="text-1 text-gray-600 mb-1.5 leading-relaxed">{course.shortDescription || course.description?.substring(0, 100)}</p>
                {course.price && (
                  <p className="text-5 font-semibold text-primary">
                    {formatCurrency(course.price, course.currency)}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-1 font-semibold text-gray-900 mb-1.5">
                  Select course dates <span className="text-red-500">*</span>
                </label>
                {(() => {
                  const availablePeriods = getBookingPeriodOptions();
                  if (availablePeriods.length === 0) {
                    return (
                      <div className="w-full px-3 py-2.5 border-2 border-red-300 rounded-xl bg-red-50 text-red-700">
                        <p className="text-1 font-medium">
                          This course has no upcoming date ranges available for booking.
                        </p>
                      </div>
                    );
                  }
                  return (
                    <select
                      required
                      value={bookingForm.selectedMonth}
                      onChange={(e) => setBookingForm({ ...bookingForm, selectedMonth: e.target.value })}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-gray-900 bg-white font-medium text-2"
                    >
                      <option value="">Select dates</option>
                      {availablePeriods.map((period) => (
                        <option key={period.value} value={period.value}>
                          {period.label}
                        </option>
                      ))}
                    </select>
                  );
                })()}
              </div>

              {course.accommodation && course.accommodation.available && course.accommodation.rooms && course.accommodation.rooms.length > 0 && (
                <div>
                  <label className="block text-1 font-semibold text-gray-900 mb-1.5">
                    Accommodation Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={bookingForm.accommodationType}
                    onChange={(e) => setBookingForm({ ...bookingForm, accommodationType: e.target.value })}
                    className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-gray-900 bg-white font-medium text-2"
                  >
                    <option value="">Select accommodation</option>
                    {course.accommodation.rooms.map((room, idx) => (
                      <option key={idx} value={room.type}>
                        {room.type} - {formatCurrency(room.price, course.currency)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-1 font-semibold text-gray-900 mb-1.5">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={bookingForm.name}
                    onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                    className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-gray-900 bg-white font-medium text-2"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label className="block text-1 font-semibold text-gray-900 mb-1.5">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={bookingForm.email}
                    onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                    className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-gray-900 bg-white font-medium text-2"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-1 font-semibold text-gray-900 mb-1.5">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={bookingForm.phone}
                  onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                  className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-gray-900 bg-white font-medium text-2"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-1 font-semibold text-gray-900 mb-1.5">
                  Additional Message
                </label>
                <textarea
                  value={bookingForm.message}
                  onChange={(e) => setBookingForm({ ...bookingForm, message: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-none text-gray-900 bg-white font-medium text-2"
                  placeholder="Any additional information or questions..."
                />
              </div>

              <div className="pt-3">
                <button
                  type="submit"
                  disabled={submittingBooking}
                  className="w-full px-5 py-2.5 bg-primary text-white rounded-xl font-semibold text-2 hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
                >
                  {submittingBooking ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Booking Request'
                  )}
                </button>
              </div>
            </form>
            </div>
          </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            touchAction: 'none',
            overflow: 'hidden',
            overscrollBehavior: 'none'
          }}
          onTouchStart={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onTouchMove={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onClick={() => setSelectedImage(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="relative bg-black rounded-2xl overflow-hidden max-w-5xl w-full"
            style={{ maxHeight: '90vh', aspectRatio: '16/9' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-10 bg-black/60 hover:bg-black/80 backdrop-blur-sm rounded-full p-2 transition-colors shadow-lg ring-2 ring-white/30"
            >
              <X className="h-6 w-6 text-white" />
            </button>
            <img
              src={selectedImage}
              alt="Course image"
              className="w-full h-full object-contain"
              onError={(e) => {
                console.error('Image load error:', e);
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                e.stopPropagation();
              }}
            />
          </motion.div>
        </div>
      )}

      {/* Video Modal */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            touchAction: 'none',
            overflow: 'hidden',
            overscrollBehavior: 'none'
          }}
          onTouchStart={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onTouchMove={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onClick={() => setSelectedVideo(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="relative bg-black rounded-2xl overflow-hidden max-w-5xl w-full"
            style={{ maxHeight: '90vh', aspectRatio: '16/9' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute top-4 right-4 z-10 bg-black/60 hover:bg-black/80 backdrop-blur-sm rounded-full p-2 transition-colors shadow-lg ring-2 ring-white/30"
            >
              <X className="h-6 w-6 text-white" />
            </button>
            <video
              src={selectedVideo}
              controls
              autoPlay
              className="w-full h-full"
              onError={(e) => {
                console.error('Video load error:', e);
                const target = e.target as HTMLVideoElement;
                target.style.display = 'none';
                e.stopPropagation();
              }}
            >
              Your browser does not support the video tag.
            </video>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default CourseDetails;
